/**
 * Which raid object to talk to. THE CLIENT'S COPY.
 *
 * The Worker has the same rules in cloudflare/src/lib/raidBossKey.ts and the
 * two must be changed together — same arrangement as the shiny roster
 * overrides, and for the same reason: there is no shared module between the
 * client bundle and the Worker.
 *
 * They have to agree exactly. If this file and its Worker twin ever disagree
 * about a boss's key, this client opens a socket to an object no host is
 * posting to: an empty raid list, no error, nothing in a log. That is the whole
 * risk of the duplication, and it is why the derivation is tiny and boring and
 * built only from identity — tier, species, form — and never from a display
 * name or a shiny flag, both of which move with the event rotation.
 *
 * Mostly the client does not need to derive anything: a lobby id already
 * carries its shard, and boss rows arrive from the index with their key
 * attached. `bossKey` is here for the one case that has neither — hosting a
 * raid, where the client picks a boss and wants to know where it will land.
 */

export interface KeyableBoss {
  speciesId: number | null;
  name: string;
  formId: string | null;
  tier: string;
}

/** The index object: boss rows, not lobbies. */
export const INDEX_SHARD = "index";

/** Ids minted before sharding. They route nowhere and 404, by design. */
export const LEGACY_SHARD = "global";

const SEP = "~";

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/** Must match the Worker's bossKey exactly. See the header. */
export function bossKey(boss: KeyableBoss): string {
  const species = boss.speciesId != null ? `s${boss.speciesId}` : `n${slug(boss.name)}`;
  const form = boss.formId ? slug(boss.formId) : "base";
  return `${slug(boss.tier)}-${species}-${form}`;
}

/** Which object owns this lobby, read straight out of its id. */
export function shardForLobbyId(id: string): string {
  const at = id.indexOf(SEP);
  if (at <= 0) return LEGACY_SHARD;
  return id.slice(0, at);
}
